import { ArrowDropDown, Check } from "@mui/icons-material";
import {
  IconButton,
  Stack,
  Paper,
  Popper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  Divider,
  Typography,
} from "@mui/material";
import { ApiMetric } from "@incendium/api";
import { publisherConversions } from "features/attributionAnalytics/constants";
import { useState, useCallback } from "react";

const StyledPopper = styled(Popper)(() => ({
  zIndex: 2,
}));

const StyledListButton = styled(ListItemButton)(({ theme }) => ({
  "& svg:not(.MuiSvgIcon-root)": {
    width: 40,
    fill: theme.palette.primary.dark,
  },
  "& .MuiListItemText-root": {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
}));

interface PublisherConversionsHeadProps {
  publisherConversion: ApiMetric;
  setPublisherConversion: (metric: ApiMetric) => void;
}

function PublisherConversionsHead({
  publisherConversion,
  setPublisherConversion,
}: PublisherConversionsHeadProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl((prev) =>
      !event || prev === event.currentTarget ? null : event.currentTarget,
    );
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleConversionChange = useCallback(
    (metric: ApiMetric) => {
      setPublisherConversion(metric);
      setAnchorEl(null);
    },
    [setPublisherConversion],
  );

  return (
    <>
      <Stack direction="row" alignItems={"center"}>
        <strong>{publisherConversions[publisherConversion]}</strong>
        <IconButton onClick={handleClick}>
          <ArrowDropDown
            sx={{
              transition: "transform 0.15s linear",
              transform: isOpen ? "rotate(180deg)" : undefined,
            }}
          />
        </IconButton>
      </Stack>

      <StyledPopper open={isOpen} anchorEl={anchorEl} placement="bottom-end">
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <List disablePadding>
              <ListItem>
                <Typography variant="subtitle2" my={1}>
                  Publisher Conversions
                </Typography>
              </ListItem>
              <Divider />
              {Object.keys(publisherConversions).map((k) => (
                <ListItem key={k} disablePadding>
                  <StyledListButton
                    onClick={() => handleConversionChange(k as ApiMetric)}
                  >
                    <ListItemText primary={publisherConversions[k]} />
                    {publisherConversion === k && (
                      <ListItemIcon>
                        <Check color="success" />
                      </ListItemIcon>
                    )}
                  </StyledListButton>
                </ListItem>
              ))}
            </List>
          </ClickAwayListener>
        </Paper>
      </StyledPopper>
    </>
  );
}

export default PublisherConversionsHead;
